import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Check,
  Description,
  Email,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined,
  Schedule,
  Send,
} from '@mui/icons-material'
import { Form, Formik, FormikHelpers, getIn } from 'formik'
import _ from 'lodash'
import { inject, observer } from 'mobx-react'
import { toJS } from 'mobx'
import moment from 'moment'
import Moment from 'moment'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import { ComponentNameConstants } from 'src/constants/ComponentNameConstants'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import * as Yup from 'yup'
import PatientActionBar from '../common/ActionBar/PatientActionBar'
import CardWithNoTitle from '../common/CardWithNoTitle/CardWithNoTitle'
import CardWithTitle from '../common/CardWithTitle/CardWithTitle'
import ESDataTableWithHeader from '../common/ESDataTableWithHeader'
import FullWidthField from '../common/FullWidthField'
import IDataTableManager from '../common/IDataTableManager'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import SelectField from '../common/SelectField/SelectField'
import { noWhitespaceString } from '../common/YupExtensions'
import { useUnsavedChangesWarning } from '../common/hooks/useUnsavedChangesWarning'
import UnsavedChangesDialog from '../common/UnsavedChangesDialog/UnsavedChangesDialog'
import {
  DefaultSchedule,
  IAppointmentTask,
  IEpisodeOfCare,
  ILocation,
  IOrganization,
  IPatient,
  IProvider,
  IReferral,
  ISchedule,
  IScheduleAppointmentTask,
} from '../Definitions'
import { IProviderDto, TestDto } from '../generated_client'
import {
  createIScheduleFormValues,
  IScheduleFormValues,
  toISchedule,
} from '../patients/Scheduling/ScheduleFormValues'
import ScheduleAppointmentTaskDialog from '../scheduleAppointmentTasks/ScheduleAppointmentTaskDialog'
import { IStores } from '../Stores'
import { OrganizationId } from '../utils/OrganizationIds'
import AuthorizationModal from './AuthorizationModal'
import PTNoteModal from './PTNoteModal'
import CriticalCareDialog from './CriticalCareDialog'
import ProviderEditDialog from './ProviderEditDialog'
import ProviderLookupDialog from './ProviderLookupDialog'
import SingleCaseAgreementDialog from './SingleCaseAgreementDialog/SingleCaseAgreementDialog'
import { QuoteStatus } from 'src/utils/QuoteStatus'
import { getHistoricalProvidersSummary } from '../ReceiveClaim/SimpleEventDialogService'
import { HistoricalProvidersSummary } from '../ReceiveClaim/HistoricalProvidersSummary'
import '../ReceiveClaim/SimpleEventDialog.css'

const DIAGNOSTIC_TEST_COMPONENT_ID = '194a2cc4-424a-43d6-b4ff-292d3b72cc9b'

const StyledTableRow = (props: any) => (
  <TableRow
    {...props}
    sx={{
      '&:nth-of-type(odd)': {
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#EEEEEE',
      },
    }}
  />
)

function parseDateString(_value: Date, originalValue: string) {
  return new Date(moment(originalValue, 'YYYY-MM-DD').toISOString())
}

const DiagnosticScheduleSchema = Yup.object().shape({
  appointmentDateInput: Yup.date().when('isTBD', (isTBD: any, schema: any) =>
    isTBD === false
      ? schema
          .transform(parseDateString)
          .min(new Date('1-1-1900'), 'Please enter a complete date')
          .required('Appointment Date is required')
      : schema
  ),
  authorizationComment: Yup.string(),
  comments: Yup.string(),
  componentId: noWhitespaceString('Component is required', true),
  isActive: Yup.boolean(),
  isLocationRequired: Yup.boolean(),
  isTBD: Yup.boolean().test('appointment-or-tbd', '', function (value) {
    const { appointmentDateInput } = this.parent
    return !!appointmentDateInput || value === true
  }),

  testIds: Yup.array().required('At least one Test is required'),
})

const SurgicalScheduleSchema = Yup.object().shape({
  appointmentDateInput: Yup.date().when('isTBD', (isTBD: any, schema: any) =>
    isTBD === false
      ? schema
          .transform(parseDateString)
          .min(new Date('1-1-1900'), 'Please enter a complete date')
          .required('Appointment Date is required')
      : schema
  ),
  authorizationComment: Yup.string(),
  comments: Yup.string(),
  componentId: noWhitespaceString('Component is required', true),
  isActive: Yup.boolean(),
  isLocationRequired: Yup.boolean(),
  isTBD: Yup.boolean().test('appointment-or-tbd', '', function (value) {
    const { appointmentDateInput } = this.parent
    return !!appointmentDateInput || value === true
  }),
})

interface IEditScheduleRouteParams {
  patientId?: string
  episodeOfCareId?: string
  referralId?: string
  scheduleId?: string
}

interface IUnsavedChangesProps {
  showCancelDialog: boolean
  handleCancelClick: (hasChanges: boolean) => void
  handleCancelConfirm: (onNavigate: () => void) => void
  handleCancelDismiss: () => void
  setIsDirty: (isDirty: boolean) => void
}

interface IEditScheduleProps extends IUnsavedChangesProps {
  routeParams: IEditScheduleRouteParams
  appointmentTasksDataTable?: IDataTableManager<IAppointmentTask>
  clearAppointmentTaskFilters?: () => void
  currentAppOrganization?: IOrganization
  createAppointmentConfirmation?: (scheduleId: string) => void
  createAuthorizationPdf?: (scheduleId: string) => void
  createResultsPdf?: (scheduleId: string) => void
  scheduleAppointmentTasksDataTable?: IDataTableManager<IScheduleAppointmentTask>
  getAllLocationsForProvider?: () => Promise<void>
  getAllComponents?: (includeInactive: boolean) => Promise<void>
  getPatientById?: (id: string) => Promise<void>
  getProviders?: (filter: string) => Promise<void>
  getReferralById?: (id: string) => Promise<void>
  getScheduleById?: (id: string) => Promise<void>
  getEpisodeOfCareById?: (id: string) => Promise<void>
  isCurrentContractSingleAgreement?: boolean
  isModalOpen?: boolean
  isProviderEditDialogOpen?: boolean
  isProviderLookupDialogOpen?: boolean
  isSingleCaseAgreementDialogOpen?: boolean
  isCriticalCareModalOpen?: boolean
  isTaskRed?: (data: IScheduleAppointmentTask) => boolean
  clearLocations?: () => void
  selectedComponent?: DropdownOption
  dropdownComponents?: DropdownOption[]
  loadAppointmentTasks?: () => Promise<void>
  loadScheduleAppointmentTasks?: () => Promise<void>
  loadProvider?: (id: string) => Promise<void[]>
  locations?: ILocation[]
  openProviderLookupDialog?: () => void
  openSingleCaseAgreementDialog?: () => void
  openCriticalCareModal?: () => void
  providers?: IProviderDto[]
  saveSchedule?: (
    schedule: ISchedule,
    eocId: string,
    scheduleId: string,
    resetUiState: boolean
  ) => Promise<string | void>
  selectedLocation?: ILocation
  selectedProvider?: IProvider
  getNcmAdjusterEmailLink?: (schedule: ISchedule) => string
  getPatientEmailLink?: (schedule: ISchedule) => string
  getTestResultsEmailLink?: (schedule: ISchedule) => string
  setIsCurrentContractSingleAgreement?: (isSingleAgreement?: boolean) => void
  setSelectedDropdownComponent?: (component?: DropdownOption) => void
  setSelectedLocation?: (
    location?: ILocation,
    reload?: () => void
  ) => void | ((location?: ILocation) => void)
  setSelectedProvider?: (provider?: IProvider) => Promise<void>
  setSelectedPatient?: (patient?: IPatient) => void
  setSelectedSchedule?: (schedule?: ISchedule) => void
  schedule?: ISchedule
  referral?: IReferral
  patient?: IPatient
  episodeOfCare?: IEpisodeOfCare
  rerouteToPath?: (path: string) => void
  getProviderContractType?: (provider?: IProvider) => Promise<void>
  isCriticalCare?: boolean
  physicianItems?: DropdownOption[]
  openDMEAuthorizationModal?: () => void
  openPTAuthorizationModal?: () => void
  openOTAuthorizationModal?: () => void
  openInHomeAuthorizationModal?: () => void
  openInpatientAuthorizationModal?: () => void
  openWCEAuthorizationModal?: () => void
  openHCEAuthorizationModal?: () => void
  openFCEAuthorizationModal?: () => void
  openPTNoteModal?: () => void
  createSurgeryFacilityAuthorization?: () => Promise<string>
  createPatientInjectionContract?: () => Promise<string>
  createPatientSurgeryContract?: () => Promise<string>
  createInitialEvalAuthorization?: () => Promise<string>
  createTakeoverOfCareAuthorization?: () => Promise<string>
  close?: (fallBack?: string) => void
  historicalProviders?: HistoricalProvidersSummary[]
}
@observer
class EditSchedule extends React.Component<IEditScheduleProps> {
  private formikSetFieldValueRef: ((field: string, value: any) => void) | null = null // Store setFieldValue from Formik

  public state = {
    expandedTasksActions: '',
    isLoading: false,
    reload: false,
    selectedPhysicianId: undefined as string | undefined,
    providerError: false,
    authLoading: false,
    hasClaims: false,
    historicalProviders: [],
    scheduleKey: '', // Stable key for Formik to prevent remounting
    initialFormValues: null as IScheduleFormValues | null, // Memoized initial values
    plainComponentOptions: [] as any[], // Stable plain options for Autocomplete
  }

  public forceUpdateHandler = (location?: ILocation, provider?: IProvider) => {
    const setFieldValue = this.formikSetFieldValueRef

    if (setFieldValue) {
      const targetLocation = location || this.props.selectedLocation
      const targetProvider = provider || this.props.selectedProvider

      setFieldValue('location', targetLocation?.name || '')
      setFieldValue('provider', targetProvider?.name || '')
      setFieldValue('locationId', targetLocation?.id || '')
      setFieldValue('providerId', targetProvider?.id || '')

      // Set component name for readonly display
      if (this.props.selectedComponent) {
        setFieldValue('componentName', this.props.selectedComponent.description || '')
      }
    }
    this.setState({ providerError: false })
    this.forceUpdate()
  }

  public save = async (
    values: IScheduleFormValues,
    formikBag: FormikHelpers<IScheduleFormValues>
  ) => {
    if (this.props.selectedLocation && this.props.selectedProvider) {
      values.locationId = this.props.selectedLocation.id
      values.providerId = this.props.selectedProvider.id
      const schedule = toISchedule(values, this.props.schedule)
      schedule.physicianId = this.state.selectedPhysicianId
      schedule.patientId = this.props.patient!.id
      schedule.episodeOfCareId = this.props.episodeOfCare!.id
      if (schedule.isNew) {
        schedule.organizationId = this.props.currentAppOrganization!.id
      }

      const scheduleId = this.props.routeParams.scheduleId || schedule.id || ''
      const cleanScheduleId = scheduleId === 'undefined' ? '' : scheduleId
      await this.props.saveSchedule!(
        schedule,
        this.props.routeParams.episodeOfCareId!,
        cleanScheduleId,
        this.state.reload
      )
        .then((response: any) => {
          formikBag.setSubmitting(false)
          if (response && this.state.reload) {
            ;(window as any)._skipBeforeUnload = true
            this.props.rerouteToPath!(
              `/patients/patient/${this.props.routeParams.patientId}/referral/${this.props.routeParams.referralId}/episodeOfCare/${this.props.routeParams.episodeOfCareId}/schedule/${response}`
            )
            setTimeout(() => window.location.reload(), 250)
          } else if (response) {
            this.props.close!(
              `/patients/patient/${this.props.routeParams.patientId}/referral/${this.props.routeParams.referralId}/episodeOfCare/${this.props.routeParams.episodeOfCareId}`
            )
          }
        })
        .catch((error) => {
          console.error('Save schedule failed:', error)
          formikBag.setSubmitting(false)
        })
        .finally(() => {
          formikBag.setSubmitting(false)
        })
    } else {
      formikBag.setSubmitting(false)
      this.setState({ providerError: true })
    }
  }

  private handleCancelClick = (dirty: boolean) => {
    if (dirty) {
      this.props.handleCancelClick(dirty)
    } else {
      this.cancel()
    }
  }

  private cancel = () => {
    this.props.close!(
      `/patients/patient/${this.props.routeParams.patientId}/referral/${this.props.routeParams.referralId}/episodeOfCare/${this.props.routeParams.episodeOfCareId}`
    )
  }

  public async setup() {
    this.forceUpdateHandler = this.forceUpdateHandler.bind(this)
    const { routeParams } = this.props

    // Set stable key for Formik to prevent remounting
    const scheduleKey = routeParams?.scheduleId || 'new'
    this.setState({ scheduleKey })

    this.setState({
      expandedTasksActions:
        routeParams && routeParams.scheduleId ? 'tasksActionsPanel' : '',
    })
    this.setState({ isLoading: true })
    let promises = [] as Array<Promise<void>>
    if (routeParams && routeParams.episodeOfCareId) {
      promises.push(this.props.getEpisodeOfCareById!(routeParams.episodeOfCareId))
    }

    if (routeParams && routeParams.referralId) {
      promises.push(this.props.getReferralById!(routeParams.referralId))
    }

    if (routeParams && routeParams.patientId) {
      promises.push(this.props.getPatientById!(routeParams.patientId))
    }

    if (routeParams && routeParams.scheduleId) {
      // Load components first, then schedule
      await this.props.getAllComponents!(true)

      promises.push(
        this.props.getScheduleById!(routeParams.scheduleId).then(() => {
          this.props.loadScheduleAppointmentTasks!()
        })
      )
    } else {
      this.props.setSelectedSchedule!(DefaultSchedule(true))
      promises.push(this.props.getAllComponents!(false))
    }

    await Promise.all(promises)
      .then(() => {
        promises = [] as Array<Promise<void>>
        // Set initial form values once to prevent recalculation
        const initialFormValues = createIScheduleFormValues(this.props.schedule!, '')

        // Convert dropdown options to plain objects once for stable references
        const dropdownOptions = this.props.schedule?.isNew
          ? this.props.dropdownComponents?.filter((x) => x.isEnabled)
          : this.props.dropdownComponents
        const plainComponentOptions = dropdownOptions ? toJS(dropdownOptions) : []

        // Set componentName for readonly display
        if (initialFormValues.componentId && plainComponentOptions.length > 0) {
          const component = plainComponentOptions.find(
            (x) => x.id === initialFormValues.componentId
          )
          if (component) {
            initialFormValues.componentName = component.description || ''
          }
        }

        this.setState({
          selectedPhysicianId: this.props.schedule?.physicianId,
          initialFormValues,
          plainComponentOptions,
        })

        // Set selected component after everything is loaded
        if (this.props.schedule?.componentId && this.props.dropdownComponents) {
          const component = this.props.dropdownComponents.find(
            (x) => x.id === this.props.schedule?.componentId
          )
          if (component) {
            this.props.setSelectedDropdownComponent!(component)
          }
        }

        this.props.setSelectedLocation!(
          this.props.schedule?.scheduleProviderLocations
            .filter((x) => x.isPrimaryLocation)
            .at(0)?.location as unknown as ILocation
        )
        this.props.setSelectedProvider!(
          this.props.schedule?.scheduleProviderLocations
            .filter((x) => x.isPrimaryLocation)
            .at(0)?.location?.provider as unknown as IProvider
        )
      })
      .catch(() => {})
      .finally(() => this.setState({ isLoading: false }))
  }

  private debouncedFetchProviders = _.debounce(async (values: IScheduleFormValues) => {
    const eocId = this.props.episodeOfCare?.id
    const componentId = values.componentId

    if (eocId && componentId) {
      const historicalProviders = await getHistoricalProvidersSummary(eocId, componentId)
      if (!_.isEqual(historicalProviders, this.state.historicalProviders)) {
        this.setState({ historicalProviders })
      }
    }
    return []
  }, 20)

  public async componentDidMount() {
    await this.setup()
  }

  public async componentDidUpdate(prevProps: IEditScheduleProps) {
    if (this.props.routeParams.scheduleId !== prevProps.routeParams.scheduleId) {
      await this.setup()
    }
  }

  public componentWillUnmount() {
    this.props.setSelectedSchedule!(undefined)
    this.props.setSelectedDropdownComponent!(undefined)
    this.props.setSelectedLocation!(undefined)
    this.props.setSelectedProvider!(undefined)
    this.props.setSelectedPatient!(undefined)
    this.props.clearAppointmentTaskFilters!()
  }

  private openProviderLookupDialog =
    (setFieldValue: (field: string, value: any) => void, values: IScheduleFormValues) =>
    () => {
      if (values.providerId) {
        setFieldValue('providerId', undefined)
      }
      if (values.locationId) {
        setFieldValue('locationId', undefined)
      }
      this.props.openProviderLookupDialog!()
    }

  public getProviderId(provider: IProvider) {
    return provider !== null ? provider.id || '' : ''
  }

  public async checkIfCritical(provider?: IProvider) {
    if (provider === undefined) {
      return
    }
    await this.props.getProviderContractType!(provider)

    if (this.props.currentAppOrganization!.id === OrganizationId.Diagnostic) {
      if (this.props.isCriticalCare!) {
        this.props.openCriticalCareModal!()
      }
    }
    return
  }

  private hasCompletedTasks(): boolean {
    if (this.props.scheduleAppointmentTasksDataTable?.data) {
      const hasCompleted = this.props.scheduleAppointmentTasksDataTable.data.some(
        (task) => task.dateCompleted !== undefined && task.dateCompleted !== null
      )
      return hasCompleted
    }
    return false
  }

  private loadComponentTasks =
    (values: IScheduleFormValues, initialValues: IScheduleFormValues) => () => {
      if (values.componentId !== initialValues.componentId) {
        if (
          this.props.selectedComponent &&
          this.props.selectedComponent.id === values.componentId
        ) {
          return
        }
        const component = this.props.dropdownComponents!.find(
          (x) => x.id === values.componentId
        )
        this.props.setSelectedDropdownComponent!(component)
        if (
          this.props.selectedProvider &&
          this.props.selectedProvider.contracts &&
          component
        ) {
          const currentContract = _.orderBy(
            this.props.selectedProvider.contracts
              .filter((x) => x.isActive)
              .filter((x) => Moment(new Date()).isAfter(x.startedOn)),
            (x) => x.startedOn,
            'desc'
          )[0]

          this.props.setIsCurrentContractSingleAgreement!(
            component!.id === DIAGNOSTIC_TEST_COMPONENT_ID &&
              currentContract &&
              currentContract.isSingleCaseAgreement
          )
        } else {
          this.props.setIsCurrentContractSingleAgreement!(false)
        }
        this.props.loadAppointmentTasks!()
      }
    }

  public getProviderName(provider: IProvider) {
    return provider !== null ? provider.name : ''
  }

  private getTestName(item: TestDto) {
    return item ? `${item.procedure!.diagnostic} (${item.bodyPart!.name})` || '' : ''
  }

  private getValue(item: any) {
    return item ? item.id || '' : ''
  }

  private clearAppointmentDate =
    (
      setFieldValue: (field: string, value: any) => void,
      values: IScheduleFormValues,
      validateForm: () => void
    ) =>
    () => {
      setFieldValue('isTBD', !values.isTBD)
      if (!values.isTBD) {
        setFieldValue('appointmentDateInput', '')
        setFieldValue('appointmentTime', '')
      }
      // Trigger validation after state updates
      setTimeout(() => validateForm(), 0)
    }

  private getSingleCaseAgreement() {
    if (
      this.props.selectedProvider &&
      this.props.selectedProvider.contracts &&
      this.props.selectedProvider.contracts.length > 0
    ) {
      const currentDate = new Date()
      const currentContracts = _.orderBy(
        this.props.selectedProvider.contracts
          .filter((x) => x.isActive)
          .filter((x) => Moment(currentDate).isAfter(x.startedOn)),
        (x) => x.startedOn,
        'desc'
      )
      if (this.props.selectedProvider.requiresSingleCaseAgreement) {
        const currentContract = currentContracts.find(
          (x) =>
            x.isSingleCaseAgreement &&
            x.preCertNumber &&
            x.preCertNumber === this.props.patient!.patientId
        )
        if (currentContract !== undefined) {
          const preCertNumber = currentContract.preCertNumber
            ? currentContract.preCertNumber
            : 'not defined'
          return `Provider requires a Single Case Agreement. Active SCA found for Patient ID #${preCertNumber} on ${Moment(
            currentContract.startedOn
          ).format('MM/DD/YYYY')}.`
        } else {
          return `Provider requires a Single Case Agreement. No Active SCA contract found for Patient ID #${
            this.props.patient!.patientId
          }`
        }
      }
    }
    return undefined
  }

  private handleTasksActionsChange =
    (panel: string) => (_: React.SyntheticEvent, __: boolean) => {
      this.setState({
        expandedTasksActions: panel === this.state.expandedTasksActions ? '' : panel,
      })
    }

  private sendAppointmentConfirmation = () => {
    if (!this.isReadonly()) {
      this.props.createAppointmentConfirmation!(this.props.schedule!.id)
    }
  }

  private createResultsPdf = () => {
    if (!this.isReadonly()) {
      this.props.createResultsPdf!(this.props.schedule!.id)
    }
  }

  private getNcmAdjusterEmailLink() {
    if (!this.isReadonly()) {
      return this.props.getNcmAdjusterEmailLink!(this.props.schedule!)
    }
    return undefined
  }

  private openSingleCaseAgreementDialog = () => {
    if (!this.isReadonly()) {
      this.props.openSingleCaseAgreementDialog!()
    }
  }

  private getPatientEmailLink() {
    if (!this.isReadonly()) {
      return this.props.getPatientEmailLink!(this.props.schedule!)
    }
    return undefined
  }

  private getTestResultsEmailLink() {
    if (!this.isReadonly()) {
      return this.props.getTestResultsEmailLink!(this.props.schedule!)
    }
    return undefined
  }

  private getRemainingTasks() {
    if (this.props.schedule) {
      if (!this.props.schedule.isNew) {
        if (this.props.scheduleAppointmentTasksDataTable) {
          const numberOfRemainingTasks =
            this.props.scheduleAppointmentTasksDataTable.data.filter(
              (x) => x.dateCompleted === undefined && x.taskNotApplicable === false
            ).length
          if (numberOfRemainingTasks > 0) {
            return `${numberOfRemainingTasks} tasks remaining`
          }
        }
      }
    }
    return 'Complete'
  }

  private isReadonly() {
    const { schedule, currentAppOrganization } = this.props
    return schedule && currentAppOrganization
      ? !schedule.isNew &&
          (schedule.organizationId !== currentAppOrganization!.id ||
            schedule.isSimpleEvent ||
            (schedule.claimEvents?.length ?? 0) > 0)
      : true
  }

  private handleComponentChange =
    (values: IScheduleFormValues, initialValues: IScheduleFormValues) => () => {
      this.loadComponentTasks(values, initialValues)
      this.debouncedFetchProviders(values)
    }

  public handleSave = (submitForm: () => void, reload: boolean) => () => {
    this.setState({ reload }, () => {
      submitForm()
    })
  }

  private handleHistoricalProviderClick =
    (
      provider: HistoricalProvidersSummary,
      setFieldValue: (field: string, value: any) => void
    ) =>
    async (e: React.MouseEvent) => {
      e.preventDefault()

      // Set form values
      setFieldValue('providerId', provider.providerGroupId)
      setFieldValue('provider', provider.providerGroupName)

      if (provider.locationName && provider.locationId) {
        setFieldValue('locationId', provider.locationId)
        setFieldValue('location', provider.locationName)
      }

      // Set selected provider/location in store for both new and existing schedules
      this.props.setSelectedProvider!({
        id: provider.providerGroupId,
        name: provider.providerGroupName,
      } as IProvider)

      if (provider.locationName && provider.locationId) {
        this.props.setSelectedLocation!({
          id: provider.locationId,
          name: provider.locationName,
        } as ILocation)
      }

      if (provider.providerGroupId) {
        this.props.getAllLocationsForProvider!()
      }
    }

  public render() {
    const { historicalProviders } = this.state
    const flattenedProviders = historicalProviders.flat()
    const {
      dropdownComponents,
      schedule,
      referral,
      patient,
      episodeOfCare,
      currentAppOrganization,
      physicianItems,
    } = this.props

    const hasTaskNotApplicable = this.props.scheduleAppointmentTasksDataTable?.data.some(
      (x) => x.taskNotApplicable === true
    )

    const isDisabled =
      this.state.hasClaims ||
      schedule?.rescheduled ||
      hasTaskNotApplicable ||
      this.hasCompletedTasks()

    const targetPhysicianItems =
      this.props.schedule && this.props.schedule.isNew
        ? (physicianItems?.filter((x) => x.isEnabled) ?? [])
        : (physicianItems ?? [])

    const hasValue = (field: string, values: IScheduleFormValues) => {
      const value = getIn(values, field)

      return !!value
    }

    // Capture isTaskRed in render to avoid reactive context issues
    const isTaskRed = this.props.isTaskRed
    const rowCellStyleFunction = (data: IScheduleAppointmentTask) => {
      if (isTaskRed && isTaskRed(data)) {
        return { color: '#CC3F3F' } as React.CSSProperties
      } else {
        return {} as React.CSSProperties
      }
    }

    const appointmentTaskDialog = this.props.isModalOpen && (
      <ScheduleAppointmentTaskDialog />
    )
    const providerLookupDialog = this.props.isProviderLookupDialogOpen && (
      <ProviderLookupDialog
        open={this.props.isProviderLookupDialogOpen}
        selectedLocation={this.props.selectedLocation}
        setSelectedLocation={this.props.setSelectedLocation}
        selectedProvider={this.props.selectedProvider}
        setSelectedProvider={this.props.setSelectedProvider}
        reloadParent={this.forceUpdateHandler}
      />
    )
    const providerEditDialog = this.props.isProviderEditDialogOpen && (
      <ProviderEditDialog open={this.props.isProviderEditDialogOpen} />
    )
    const singleCaseAgreementDialog = this.props.isSingleCaseAgreementDialogOpen && (
      <SingleCaseAgreementDialog />
    )
    const readonly = this.isReadonly()
    const title =
      this.props.schedule && this.props.schedule.isNew
        ? 'Add New Schedule'
        : readonly
          ? 'View Schedule'
          : 'Edit Schedule'

    const quoteIsValid =
      this.props.schedule?.episodeOfCare?.quote !== undefined &&
      (this.props.schedule!.episodeOfCare!.quote!.status! === QuoteStatus.Quoted ||
        this.props.schedule?.episodeOfCare.quote.status === QuoteStatus.NeedsRecon ||
        this.props.schedule?.episodeOfCare.quote.status === QuoteStatus.Reconciled)

    return (
      <>
        <Helmet>
          <title>
            {title +
              ': ' +
              this.props.patient?.lastName +
              ', ' +
              this.props.patient?.firstName +
              ' (' +
              moment(this.props.patient?.dateOfBirth).format('MM/DD/YYYY') +
              ') ' +
              episodeOfCare?.episodeOfCareNumberOrLegacyId}
          </title>
        </Helmet>
        {providerLookupDialog}
        {providerEditDialog}
        {singleCaseAgreementDialog}
        {!this.state.isLoading &&
        schedule &&
        episodeOfCare &&
        dropdownComponents &&
        this.state.initialFormValues &&
        this.state.plainComponentOptions.length > 0 ? (
          <>
            <Formik
              key={this.state.scheduleKey}
              enableReinitialize={false}
              initialValues={this.state.initialFormValues}
              onSubmit={this.save}
              validateOnChange={true}
              validationSchema={
                (schedule.isNew &&
                  currentAppOrganization!.id === OrganizationId.Diagnostic) ||
                (!schedule.isNew && schedule.organizationId === OrganizationId.Diagnostic)
                  ? DiagnosticScheduleSchema
                  : SurgicalScheduleSchema
              }
            >
              {({
                errors,
                initialValues,
                setFieldValue,
                values,
                submitForm,
                isValid,
                dirty,
                validateForm,
              }) => {
                // Store setFieldValue in ref for use outside the render prop
                this.formikSetFieldValueRef = setFieldValue

                // Update isDirty through props
                React.useEffect(() => {
                  this.props.setIsDirty(dirty)
                }, [dirty])

                return (
                  <Form>
                    <AuthorizationModal />
                    <PTNoteModal />
                    {this.state.authLoading && (
                      <div
                        style={{
                          position: 'fixed',
                          display: 'flex',
                          width: '100%',
                          height: '100%',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          cursor: 'pointer',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1500,
                        }}
                      >
                        <CircularProgress size={100} />
                      </div>
                    )}
                    <ResponsiveAppBar title={title} pageIcon={<Schedule />}>
                      <Grid
                        container
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                      >
                        {this.state.isLoading ? (
                          <Grid item>
                            <CircularProgress size={24} />
                          </Grid>
                        ) : (
                          <></>
                        )}
                        <Grid item>
                          <Button
                            size="small"
                            color="inherit"
                            variant="outlined"
                            onClick={() => this.handleCancelClick(dirty)}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            size="small"
                            color="secondary"
                            variant="contained"
                            onClick={this.handleSave(submitForm, true)}
                            disabled={
                              !isValid ||
                              this.state.providerError ||
                              !values.provider ||
                              !values.location ||
                              (schedule.isNew
                                ? !dirty
                                : !dirty &&
                                  this.state.selectedPhysicianId == schedule.physicianId)
                            }
                          >
                            Save
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            size="small"
                            color="secondary"
                            variant="contained"
                            onClick={this.handleSave(submitForm, false)}
                            disabled={
                              !isValid ||
                              this.state.providerError ||
                              !values.provider ||
                              !values.location ||
                              (schedule.isNew
                                ? !dirty
                                : !dirty &&
                                  this.state.selectedPhysicianId == schedule.physicianId)
                            }
                          >
                            Save and Close
                          </Button>
                        </Grid>
                      </Grid>
                    </ResponsiveAppBar>
                    <PatientActionBar isDemographicsVisible={true} />
                    <Grid>
                      <Grid item xs={12}>
                        <CardWithNoTitle>
                          {patient && referral && episodeOfCare ? (
                            <Grid container spacing={1} style={{ marginTop: '10px' }}>
                              <Grid item md={2} xs={12}>
                                {readonly || (isDisabled && !schedule.isNew) ? (
                                  <FullWidthField
                                    inputProps={{ style: { height: '24px' } }}
                                    variant="outlined"
                                    name="componentName"
                                    label="Component"
                                    disabled
                                  />
                                ) : (
                                  <FormControl
                                    variant="outlined"
                                    style={{ width: '100%' }}
                                  >
                                    <Autocomplete
                                      size="small"
                                      disableClearable
                                      id="componentId"
                                      options={this.state.plainComponentOptions}
                                      getOptionDisabled={(option: any) =>
                                        !option.isEnabled
                                      }
                                      getOptionLabel={(option: any) =>
                                        option.description || ''
                                      }
                                      isOptionEqualToValue={(option: any, value: any) =>
                                        option.id === value.id
                                      }
                                      value={
                                        this.state.plainComponentOptions.find(
                                          (x) => x.id === values.componentId
                                        ) || null
                                      }
                                      onChange={(_: any, value: any) => {
                                        setFieldValue(
                                          'componentId',
                                          value ? value.id : '',
                                          false
                                        )
                                        setTimeout(() => {
                                          this.handleComponentChange(
                                            {
                                              ...values,
                                              componentId: value ? value.id : '',
                                            },
                                            initialValues
                                          )()
                                        }, 0)
                                      }}
                                      renderInput={(params: any) => (
                                        <TextField
                                          {...params}
                                          label="Component"
                                          variant="outlined"
                                          required
                                          InputLabelProps={{ shrink: true }}
                                          error={!!errors.componentId}
                                          helperText={errors.componentId}
                                        />
                                      )}
                                      disabled={
                                        readonly || (isDisabled && !schedule.isNew)
                                      }
                                    />
                                  </FormControl>
                                )}
                              </Grid>
                              {(schedule.isNew &&
                                currentAppOrganization!.id ===
                                  OrganizationId.Diagnostic) ||
                              (!schedule.isNew &&
                                schedule.organizationId === OrganizationId.Diagnostic) ? (
                                <>
                                  <Grid item md={2} xs={12}>
                                    <SelectField
                                      inputId="testIds"
                                      getName={this.getTestName}
                                      getValue={this.getValue}
                                      items={episodeOfCare.tests.filter(
                                        (x) =>
                                          x.scheduleTests &&
                                          (x.scheduleTests.length === 0 ||
                                            x.scheduleTests[0].scheduleId === schedule.id)
                                      )}
                                      label="Test"
                                      name="testIds"
                                      fullWidth
                                      shrinkLabel={true}
                                      required
                                      errorMessage={errors.testIds}
                                      disabled={readonly}
                                      outlined
                                      multiple
                                      checkedItems={values.testIds}
                                      inputProps={{ style: { height: '24px' } }}
                                    />
                                  </Grid>
                                </>
                              ) : (
                                <></>
                              )}
                              <Grid item md={4} xs={12}>
                                <FullWidthField
                                  inputProps={{ style: { height: '24px' } }}
                                  label="Provider"
                                  name="provider"
                                  variant="outlined"
                                  disabled
                                />
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    height: '40px',
                                  }}
                                >
                                  <Button
                                    style={{
                                      textTransform: 'none',
                                      minWidth: '280px',
                                    }}
                                    onClick={this.openProviderLookupDialog(
                                      setFieldValue,
                                      values
                                    )}
                                    variant="contained"
                                    color="secondary"
                                    disabled={readonly}
                                  >
                                    Select Provider Group and Location
                                  </Button>
                                  {flattenedProviders.length > 0 &&
                                    (!readonly || !isDisabled || schedule.isNew) && (
                                      <div
                                        style={{
                                          marginLeft: '20px',
                                        }}
                                      >
                                        <div className="historical-providers-title">
                                          Select From Previous Providers:
                                        </div>
                                        <div className="historical-providers-container">
                                          {flattenedProviders.map(
                                            (
                                              provider: HistoricalProvidersSummary,
                                              index
                                            ) => (
                                              <div
                                                key={index}
                                                className="historical-provider"
                                              >
                                                <a
                                                  href="#"
                                                  onClick={this.handleHistoricalProviderClick(
                                                    provider,
                                                    setFieldValue
                                                  )}
                                                  className="provider-link"
                                                >
                                                  {typeof provider.providerGroupName ===
                                                  'string'
                                                    ? `${provider.providerGroupName} | ${provider.locationName}, [NPI: ${provider.npi}]`
                                                    : String(provider.providerGroupName)}
                                                </a>
                                                &nbsp;&nbsp;
                                                <Tooltip
                                                  title={
                                                    <span>
                                                      {provider?.billingAddress ? (
                                                        <>
                                                          Billing Address:{' '}
                                                          {provider.billingAddress.name} -{' '}
                                                          {
                                                            provider.billingAddress
                                                              .fullAddress
                                                          }
                                                          <br />
                                                          {
                                                            provider.billingAddress.city
                                                          },{' '}
                                                          {provider.billingAddress.state}{' '}
                                                          {
                                                            provider.billingAddress
                                                              .zipCode
                                                          }
                                                        </>
                                                      ) : (
                                                        'No Billing Address Found'
                                                      )}
                                                    </span>
                                                  }
                                                >
                                                  <InfoOutlined
                                                    fontSize="small"
                                                    style={{
                                                      marginRight: '5px',
                                                      cursor: 'pointer',
                                                      color: '#1976d2',
                                                      zIndex: 1000,
                                                    }}
                                                  />
                                                </Tooltip>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </Grid>
                              <Grid item md={3} xs={12}>
                                <FullWidthField
                                  inputProps={{ style: { height: '24px' } }}
                                  label="Location"
                                  name="location"
                                  variant="outlined"
                                  disabled
                                />
                                <Grid item justifyContent="flex-end" className="error">
                                  {this.state.providerError
                                    ? 'Provider group and location are required.'
                                    : ''}
                                </Grid>
                              </Grid>
                              <Grid item md={3} xs={12}>
                                <FormControl variant="outlined" style={{ width: '100%' }}>
                                  <Autocomplete
                                    size="small"
                                    id="physicianSelect"
                                    options={targetPhysicianItems}
                                    getOptionLabel={(option: any) => option.description}
                                    value={
                                      targetPhysicianItems.find(
                                        (x) => x.id === this.state.selectedPhysicianId
                                      ) || null
                                    }
                                    onChange={(_: any, value: any) => {
                                      this.setState({
                                        selectedPhysicianId: value ? value.id : undefined,
                                      })
                                    }}
                                    renderInput={(params: any) => (
                                      <TextField
                                        {...params}
                                        label="Physician/Surgeon"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    )}
                                    disabled={readonly}
                                  />
                                </FormControl>
                              </Grid>
                              <Grid item container lg={4} md={12} xs={12} spacing={1}>
                                <Grid item xs={6}>
                                  <FullWidthField
                                    inputProps={{ style: { height: '24px' } }}
                                    format="YYYY-MM-DD"
                                    type="date"
                                    name="appointmentDateInput"
                                    label="Appointment Date"
                                    inputId="appointmentDateInput"
                                    fullWidth={true}
                                    clearable={true}
                                    shrink={hasValue('appointmentDateInput', values)}
                                    required={!values.isTBD}
                                    variant="outlined"
                                    errorMessage={errors.appointmentDateInput}
                                    disabled={readonly || values.isTBD}
                                  />
                                </Grid>

                                {!schedule.isSimpleEvent ? (
                                  <>
                                    <Grid item xs={6}>
                                      <FormControlLabel
                                        style={{ marginTop: '8px' }}
                                        control={
                                          <Checkbox
                                            checked={values.isTBD}
                                            color="secondary"
                                            name="isTBD"
                                            onChange={this.clearAppointmentDate(
                                              setFieldValue,
                                              values,
                                              validateForm
                                            )}
                                            value="isTBD"
                                            id="isTBD"
                                            disabled={readonly}
                                          />
                                        }
                                        label="Is TBD"
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FullWidthField
                                        inputProps={{ style: { height: '24px' } }}
                                        value={values.appointmentTime || ''}
                                        format="hh:mm:ss"
                                        type="time"
                                        name="appointmentTime"
                                        label="Appointment Time"
                                        inputId="appointmentTime"
                                        fullWidth={true}
                                        clearable={true}
                                        shrink={hasValue('appointmentTime', values)}
                                        variant="outlined"
                                        disabled={readonly || values.isTBD}
                                      />
                                    </Grid>
                                  </>
                                ) : (
                                  <></>
                                )}
                              </Grid>
                              {(schedule.isNew &&
                                currentAppOrganization!.id ===
                                  OrganizationId.Diagnostic) ||
                              (!schedule.isNew &&
                                schedule.organizationId === OrganizationId.Diagnostic) ? (
                                <>
                                  <Grid item container lg={8} md={12} xs={12} spacing={2}>
                                    <Grid item xs={12}>
                                      <FullWidthField
                                        label="Authorization Comment"
                                        name="authorizationComment"
                                        variant="outlined"
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <FullWidthField
                                        label="Appointment Confirmation Comment"
                                        name="appointmentConfirmationComment"
                                        variant="outlined"
                                      />
                                    </Grid>
                                  </Grid>
                                </>
                              ) : (
                                <></>
                              )}
                              <Grid item xs={12}>
                                <span style={{ color: 'red' }}>
                                  {this.getSingleCaseAgreement()}
                                </span>
                              </Grid>
                              <Grid item xs={12}>
                                <FullWidthField
                                  shrink={hasValue('comments', values)}
                                  name="comments"
                                  label="Comments"
                                  rows="3"
                                  multiline={true}
                                  variant="outlined"
                                />
                              </Grid>
                            </Grid>
                          ) : (
                            <></>
                          )}

                          <div style={{ width: '100vw' }}>
                            {schedule && !schedule.isNew ? (
                              <Accordion
                                style={{ marginTop: '10px' }}
                                sx={{
                                  boxShadow: (theme) =>
                                    theme.palette.mode === 'dark'
                                      ? '0 0 0 1px rgba(255, 255, 255, 0.12)'
                                      : undefined,
                                }}
                                expanded={
                                  this.state.expandedTasksActions === 'tasksActionsPanel'
                                }
                                onChange={this.handleTasksActionsChange(
                                  'tasksActionsPanel'
                                )}
                              >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography color="primary" variant="body2">
                                    APPOINTMENT TASKS AND ACTIONS
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Grid container>
                                    <Grid item md={8} xs={12}>
                                      <CardWithTitle
                                        title={
                                          schedule.isNew
                                            ? 'Component Tasks'
                                            : `Appointment Tasks (${this.getRemainingTasks()})`
                                        }
                                        icon={Check}
                                      >
                                        {schedule.isNew ? (
                                          this.props.selectedComponent ? (
                                            <ESDataTableWithHeader
                                              dataTableManager={
                                                this.props.appointmentTasksDataTable!
                                              }
                                              enableReorderColumns={false}
                                              enableSearch={false}
                                              enableShowInactives={true}
                                              enableToggleColumns={false}
                                              elevation={0}
                                              padding={0}
                                            />
                                          ) : (
                                            <></>
                                          )
                                        ) : (
                                          <>
                                            {appointmentTaskDialog}
                                            <ESDataTableWithHeader
                                              dataTableManager={
                                                this.props
                                                  .scheduleAppointmentTasksDataTable!
                                              }
                                              enableReorderColumns={false}
                                              enableSearch={false}
                                              enableShowInactives={true}
                                              enableToggleColumns={false}
                                              elevation={0}
                                              padding={0}
                                              rowCellStyleFunction={rowCellStyleFunction}
                                            />
                                          </>
                                        )}
                                      </CardWithTitle>
                                    </Grid>
                                    <Grid item md={4} xs={12}>
                                      <CardWithTitle title="Actions" icon={Send}>
                                        {this.props.schedule &&
                                        !this.props.schedule.isNew ? (
                                          <Table>
                                            <TableBody>
                                              {schedule &&
                                              schedule.organizationId ===
                                                OrganizationId.Diagnostic ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    <Grid
                                                      container
                                                      direction="row"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                      onClick={
                                                        this.sendAppointmentConfirmation
                                                      }
                                                      style={{ cursor: 'pointer' }}
                                                    >
                                                      <Grid item>
                                                        <Typography
                                                          color="primary"
                                                          component="span"
                                                        >
                                                          Appointment Confirmation
                                                        </Typography>
                                                      </Grid>
                                                      <Grid item>
                                                        <Description
                                                          style={{
                                                            color: '#C0C0C0',
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule &&
                                              schedule.organizationId ===
                                                OrganizationId.Diagnostic ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    <Grid
                                                      container
                                                      direction="row"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                    >
                                                      <Grid item>
                                                        <a
                                                          href={this.getNcmAdjusterEmailLink()}
                                                        >
                                                          Email NCM and Adjuster
                                                        </a>
                                                      </Grid>
                                                      <Grid item>
                                                        <Email
                                                          style={{
                                                            color: '#C0C0C0',
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule.componentName ===
                                              ComponentNameConstants.DME ? (
                                                <>
                                                  <StyledTableRow>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        onClick={
                                                          this.props
                                                            .openDMEAuthorizationModal
                                                        }
                                                        style={{ cursor: 'pointer' }}
                                                      >
                                                        <Grid item>
                                                          <Typography
                                                            color="primary"
                                                            component="span"
                                                          >
                                                            DME Authorization
                                                          </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                          <Description
                                                            style={{
                                                              color: '#C0C0C0',
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </StyledTableRow>
                                                </>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule.componentName ===
                                              ComponentNameConstants.Surgery ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    {!quoteIsValid ? (
                                                      <Tooltip
                                                        title={
                                                          'Quote is not in a valid status.'
                                                        }
                                                      >
                                                        <Grid
                                                          container
                                                          direction="row"
                                                          justifyContent="space-between"
                                                          alignItems="center"
                                                          style={{
                                                            cursor: 'default',
                                                          }}
                                                        >
                                                          <Grid item>
                                                            <span
                                                              style={{
                                                                color: 'grey',
                                                              }}
                                                            >
                                                              Agreement for Surgical
                                                              Injection Services
                                                            </span>
                                                          </Grid>
                                                          <Grid item>
                                                            <Description
                                                              style={{
                                                                color: '#C0C0C0',
                                                              }}
                                                            />
                                                          </Grid>
                                                        </Grid>
                                                      </Tooltip>
                                                    ) : (
                                                      <>
                                                        <Grid
                                                          container
                                                          direction="row"
                                                          justifyContent="space-between"
                                                          alignItems="center"
                                                          onClick={async () => {
                                                            this.setState({
                                                              authLoading: true,
                                                            })
                                                            await this.props
                                                              .createPatientInjectionContract!().then(
                                                              () =>
                                                                this.setState({
                                                                  authLoading: false,
                                                                })
                                                            )
                                                          }}
                                                          style={{
                                                            cursor: 'pointer',
                                                          }}
                                                        >
                                                          <Grid item>
                                                            <Typography
                                                              color="primary"
                                                              component="span"
                                                            >
                                                              Agreement for Surgical
                                                              Injection Services
                                                            </Typography>
                                                          </Grid>
                                                          <Grid item>
                                                            <Description
                                                              style={{
                                                                color: '#C0C0C0',
                                                              }}
                                                            />
                                                          </Grid>
                                                        </Grid>
                                                      </>
                                                    )}
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule.componentName ===
                                              ComponentNameConstants.Surgery ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    {!quoteIsValid ? (
                                                      <Tooltip
                                                        title={
                                                          'Quote is not in a valid status.'
                                                        }
                                                      >
                                                        <Grid
                                                          container
                                                          direction="row"
                                                          justifyContent="space-between"
                                                          alignItems="center"
                                                          style={{
                                                            cursor: 'default',
                                                          }}
                                                        >
                                                          <Grid item>
                                                            <span
                                                              style={{
                                                                color: 'grey',
                                                              }}
                                                            >
                                                              Agreement for Surgical
                                                              Services
                                                            </span>
                                                          </Grid>
                                                          <Grid item>
                                                            <Description
                                                              style={{
                                                                color: '#C0C0C0',
                                                              }}
                                                            />
                                                          </Grid>
                                                        </Grid>
                                                      </Tooltip>
                                                    ) : (
                                                      <>
                                                        <Grid
                                                          container
                                                          direction="row"
                                                          justifyContent="space-between"
                                                          alignItems="center"
                                                          onClick={async () => {
                                                            this.setState({
                                                              authLoading: true,
                                                            })
                                                            await this.props
                                                              .createPatientSurgeryContract!().then(
                                                              () =>
                                                                this.setState({
                                                                  authLoading: false,
                                                                })
                                                            )
                                                          }}
                                                          style={{
                                                            cursor: 'pointer',
                                                          }}
                                                        >
                                                          <Grid item>
                                                            <Typography
                                                              color="primary"
                                                              component="span"
                                                            >
                                                              Agreement for Surgical
                                                              Services
                                                            </Typography>
                                                          </Grid>
                                                          <Grid item>
                                                            <Description
                                                              style={{
                                                                color: '#C0C0C0',
                                                              }}
                                                            />
                                                          </Grid>
                                                        </Grid>
                                                      </>
                                                    )}
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule.componentName ===
                                                ComponentNameConstants.Facility ||
                                              schedule.componentName ===
                                                ComponentNameConstants.Surgery ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    <Grid
                                                      container
                                                      direction="row"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                      onClick={async () => {
                                                        this.setState({
                                                          authLoading: true,
                                                        })
                                                        await this.props
                                                          .createSurgeryFacilityAuthorization!().then(
                                                          () =>
                                                            this.setState({
                                                              authLoading: false,
                                                            })
                                                        )
                                                      }}
                                                      style={{ cursor: 'pointer' }}
                                                    >
                                                      <Grid item>
                                                        <Typography
                                                          color="primary"
                                                          component="span"
                                                        >
                                                          Surgery / Facility Authorization
                                                        </Typography>
                                                      </Grid>
                                                      <Grid item>
                                                        <Description
                                                          style={{
                                                            color: '#C0C0C0',
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule.componentName ===
                                              ComponentNameConstants.PTLocation ? (
                                                <>
                                                  <StyledTableRow>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        onClick={
                                                          this.props
                                                            .openFCEAuthorizationModal
                                                        }
                                                        style={{
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <Typography
                                                            color="primary"
                                                            component="span"
                                                          >
                                                            FCE Authorization
                                                          </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                          <Description
                                                            style={{
                                                              color: '#C0C0C0',
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </StyledTableRow>
                                                  <StyledTableRow>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        onClick={
                                                          this.props
                                                            .openInHomeAuthorizationModal
                                                        }
                                                        style={{
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <Typography
                                                            color="primary"
                                                            component="span"
                                                          >
                                                            In Home Physical Therapy
                                                            Authorization
                                                          </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                          <Description
                                                            style={{
                                                              color: '#C0C0C0',
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </StyledTableRow>
                                                  <StyledTableRow>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        onClick={
                                                          this.props
                                                            .openInpatientAuthorizationModal
                                                        }
                                                        style={{
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <Typography
                                                            color="primary"
                                                            component="span"
                                                          >
                                                            Inpatient Rehab Authorization
                                                          </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                          <Description
                                                            style={{
                                                              color: '#C0C0C0',
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </StyledTableRow>
                                                  <StyledTableRow>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        onClick={
                                                          this.props
                                                            .openOTAuthorizationModal
                                                        }
                                                        style={{
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <Typography
                                                            color="primary"
                                                            component="span"
                                                          >
                                                            Occupational Therapy
                                                            Authorization
                                                          </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                          <Description
                                                            style={{
                                                              color: '#C0C0C0',
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </StyledTableRow>
                                                  <StyledTableRow>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        onClick={
                                                          this.props
                                                            .openPTAuthorizationModal
                                                        }
                                                        style={{
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <Typography
                                                            color="primary"
                                                            component="span"
                                                          >
                                                            Physical Therapy Authorization
                                                          </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                          <Description
                                                            style={{
                                                              color: '#C0C0C0',
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </StyledTableRow>
                                                  <StyledTableRow>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        onClick={
                                                          this.props
                                                            .openWCEAuthorizationModal
                                                        }
                                                        style={{
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <Typography
                                                            color="primary"
                                                            component="span"
                                                          >
                                                            Work Conditioning
                                                            Authorization
                                                          </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                          <Description
                                                            style={{
                                                              color: '#C0C0C0',
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </StyledTableRow>
                                                  <StyledTableRow>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        onClick={
                                                          this.props
                                                            .openHCEAuthorizationModal
                                                        }
                                                        style={{
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <Typography
                                                            color="primary"
                                                            component="span"
                                                          >
                                                            Work Hardening Authorization
                                                          </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                          <Description
                                                            style={{
                                                              color: '#C0C0C0',
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </StyledTableRow>
                                                  <StyledTableRow>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        onClick={
                                                          this.props.openPTNoteModal
                                                        }
                                                        style={{
                                                          cursor: 'pointer',
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <Typography
                                                            color="primary"
                                                            component="span"
                                                          >
                                                            PT Note
                                                          </Typography>
                                                        </Grid>
                                                        <Grid item>
                                                          <Description
                                                            style={{
                                                              color: '#C0C0C0',
                                                            }}
                                                          />
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </StyledTableRow>
                                                </>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule.componentName ===
                                              ComponentNameConstants.InitialEval ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    <Grid
                                                      container
                                                      direction="row"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                      onClick={async () => {
                                                        this.setState({
                                                          authLoading: true,
                                                        })
                                                        await this.props
                                                          .createInitialEvalAuthorization!().then(
                                                          () =>
                                                            this.setState({
                                                              authLoading: false,
                                                            })
                                                        )
                                                      }}
                                                      style={{ cursor: 'pointer' }}
                                                    >
                                                      <Grid item>
                                                        <Typography
                                                          color="primary"
                                                          component="span"
                                                        >
                                                          Initial Eval Authorization
                                                        </Typography>
                                                      </Grid>
                                                      <Grid item>
                                                        <Description
                                                          style={{
                                                            color: '#C0C0C0',
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule.componentName ===
                                              ComponentNameConstants.TakeoverOfCare ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    <Grid
                                                      container
                                                      direction="row"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                      onClick={async () => {
                                                        this.setState({
                                                          authLoading: true,
                                                        })
                                                        await this.props
                                                          .createTakeoverOfCareAuthorization!().then(
                                                          () =>
                                                            this.setState({
                                                              authLoading: false,
                                                            })
                                                        )
                                                      }}
                                                      style={{ cursor: 'pointer' }}
                                                    >
                                                      <Grid item>
                                                        <Typography
                                                          color="primary"
                                                          component="span"
                                                        >
                                                          Takeover of Care Authorization
                                                        </Typography>
                                                      </Grid>
                                                      <Grid item>
                                                        <Description
                                                          style={{
                                                            color: '#C0C0C0',
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule &&
                                              schedule.organizationId ===
                                                OrganizationId.Diagnostic ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    <Grid
                                                      container
                                                      direction="row"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                      onClick={
                                                        this.openSingleCaseAgreementDialog
                                                      }
                                                      style={{ cursor: 'pointer' }}
                                                    >
                                                      <Grid item>
                                                        <Typography
                                                          color="primary"
                                                          component="span"
                                                        >
                                                          Single Case Agreement
                                                        </Typography>
                                                      </Grid>
                                                      <Grid item>
                                                        <Description
                                                          style={{
                                                            color: '#C0C0C0',
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule &&
                                              schedule.organizationId ===
                                                OrganizationId.Diagnostic ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    <Grid
                                                      container
                                                      direction="row"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                      onClick={this.createResultsPdf}
                                                      style={{ cursor: 'pointer' }}
                                                    >
                                                      <Grid item>
                                                        <Typography
                                                          color="primary"
                                                          component="span"
                                                        >
                                                          Request Results Letter
                                                        </Typography>
                                                      </Grid>
                                                      <Grid item>
                                                        <Description
                                                          style={{
                                                            color: '#C0C0C0',
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule &&
                                              schedule.organizationId ===
                                                OrganizationId.Diagnostic ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    <Grid
                                                      container
                                                      direction="row"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                    >
                                                      <Grid item>
                                                        <a
                                                          href={this.getTestResultsEmailLink()}
                                                        >
                                                          Distribute Test Results Email
                                                        </a>
                                                      </Grid>
                                                      <Grid item>
                                                        <Email
                                                          style={{
                                                            color: '#C0C0C0',
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                              {schedule &&
                                              schedule.organizationId ===
                                                OrganizationId.Diagnostic ? (
                                                <StyledTableRow>
                                                  <TableCell>
                                                    <Grid
                                                      container
                                                      direction="row"
                                                      justifyContent="space-between"
                                                      alignItems="center"
                                                    >
                                                      <Grid item>
                                                        <a
                                                          href={
                                                            this.getPatientEmailLink() ||
                                                            undefined
                                                          }
                                                        >
                                                          Patient Email
                                                        </a>
                                                      </Grid>
                                                      <Grid item>
                                                        <Description
                                                          style={{
                                                            color: '#C0C0C0',
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </TableCell>
                                                </StyledTableRow>
                                              ) : (
                                                <></>
                                              )}
                                            </TableBody>
                                          </Table>
                                        ) : (
                                          <></>
                                        )}
                                      </CardWithTitle>
                                    </Grid>
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            ) : (
                              <></>
                            )}
                          </div>
                        </CardWithNoTitle>
                      </Grid>
                    </Grid>
                    {
                      <CriticalCareDialog
                        setSelectedLocation={this.props.setSelectedLocation}
                        setSelectedProvider={this.props.setSelectedProvider}
                        clearLocations={this.props.clearLocations}
                      />
                    }
                  </Form>
                )
              }}
            </Formik>
          </>
        ) : (
          <></>
        )}
      </>
    )
  }
}

const InjectedEditSchedule = inject<
  IStores,
  IEditScheduleProps,
  Partial<IEditScheduleProps>,
  any
>((stores: IStores) => ({
  appointmentTasksDataTable: stores.appointmentTasks.dataTableStore,
  clearAppointmentTaskFilters: stores.appointmentTasks.clearFilters,
  clearLocations: stores.locations.clearLocations,
  component: stores.components.selectedComponent,
  dropdownComponents: stores.components.componentOptions,
  createAppointmentConfirmation: stores.schedules.createAppointmentConfirmation,
  createAuthorizationPdf: stores.schedules.createAuthorizationPdf,
  createResultsPdf: stores.schedules.createResultsPdf,
  currentAppOrganization: stores.global.currentAppOrganization,
  episodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
  getAllComponents: stores.components.getScheduleComponentOptions,
  getAllLocationsForProvider: stores.locations.getAllLocationsForProvider,
  getEpisodeOfCareById: stores.patientEdit.getEpisodeOfCareById,
  getNcmAdjusterEmailLink: stores.schedules.getNcmAdjusterEmailLink,
  getPatientById: stores.patients.getPatientById,
  getPatientEmailLink: stores.schedules.getPatientEmailLink,
  getProviderContractType: (provider?: IProvider) =>
    provider ? stores.providers.getProviderContractType(provider) : Promise.resolve(),
  getProviders: stores.providers.getProviderSuggestions,
  getReferralById: stores.referrals.getReferralWithContactsById,
  getScheduleById: stores.schedules.getScheduleById,
  getTestResultsEmailLink: stores.schedules.getTestResultsEmailLink,
  isCriticalCare: stores.providers.isCriticalCare,
  isCriticalCareModalOpen: stores.providers.isCriticalCareModalOpen,
  isCurrentContractSingleAgreement:
    stores.appointmentTasks.isCurrentContractSingleAgreement,
  isModalOpen: stores.scheduleAppointmentTasks.isModalOpen,
  isProviderEditDialogOpen: stores.schedules.isProviderEditDialogOpen,
  isProviderLookupDialogOpen: stores.schedules.isProviderLookupDialogOpen,
  isSingleCaseAgreementDialogOpen: stores.schedules.isSingleCaseAgreementDialogOpen,
  isTaskRed: stores.scheduleAppointmentTasks.isTaskRed,
  loadAppointmentTasks: stores.appointmentTasks.getColumnSettingsAndAppointmentTasks,
  loadProvider: stores.providers.loadProvider,
  loadScheduleAppointmentTasks:
    stores.scheduleAppointmentTasks.getColumnSettingsAndScheduleAppointmentTasks,
  locations: stores.locations.locations,
  openCriticalCareModal: stores.providers.openCriticalCareModal,
  openProviderLookupDialog: stores.schedules.openProviderLookupDialog,
  openSingleCaseAgreementDialog: stores.schedules.openSingleCaseAgreementDialog,
  patient: stores.patients.selectedPatient,
  physicianItems: stores.locations.physicianItems,
  providers: stores.providers.providers,
  referral: stores.referrals.selectedReferral,
  rerouteToPath: stores.global.rerouteToPath,
  saveSchedule: stores.schedules.saveSchedule,
  schedule: stores.schedules.selectedSchedule,
  scheduleAppointmentTasksDataTable: stores.scheduleAppointmentTasks.dataTableStore,
  selectedLocation: stores.locations.selectedEditScheduleLocation,
  selectedProvider: stores.providers.selectedEditScheduleProvider,
  setIsCurrentContractSingleAgreement:
    stores.appointmentTasks.setIsCurrentContractSingleAgreement,
  setSelectedDropdownComponent: stores.components.setSelectedDropdownComponent,
  setSelectedLocation: stores.locations.setSelectedEditScheduleLocation,
  setSelectedProvider: stores.providers.setSelectedEditScheduleProvider,
  setSelectedSchedule: (schedule?: ISchedule) => {
    if (schedule) {
      stores.schedules.setSelectedSchedule(schedule)
    }
  },
  openDMEAuthorizationModal: stores.schedules.openDMEAuthorizationModal,
  openPTAuthorizationModal: stores.schedules.openPTAuthorizationModal,
  openOTAuthorizationModal: stores.schedules.openOTAuthorizationModal,
  openInHomeAuthorizationModal: stores.schedules.openInHomeAuthorizationModal,
  openInpatientAuthorizationModal: stores.schedules.openInpatientAuthorizationModal,
  openWCEAuthorizationModal: stores.schedules.openWCEAuthorizationModal,
  openHCEAuthorizationModal: stores.schedules.openHCEAuthorizationModal,
  openFCEAuthorizationModal: stores.schedules.openFCEAuthorizationModal,
  openPTNoteModal: stores.schedules.openPTNoteModal,
  createSurgeryFacilityAuthorization: stores.schedules.createSurgeryFacilityAuthorization,
  createPatientInjectionContract: stores.schedules.createPatientInjectionContract,
  createPatientSurgeryContract: stores.schedules.createPatientSurgeryContract,
  createInitialEvalAuthorization: stores.schedules.createInitialEvalAuthorization,
  createTakeoverOfCareAuthorization: stores.schedules.createTakeoverOfCareAuthorization,
  close: stores.global.closeTab,
  setSelectedPatient: stores.patients.setSelectedPatient,
}))(EditSchedule)

const EditScheduleWithRouter: React.FC = () => {
  const params = useParams<{
    patientId?: string
    episodeOfCareId?: string
    referralId?: string
    scheduleId?: string
  }>()
  const [isDirty, setIsDirty] = React.useState(false)
  const componentRef = React.useRef<EditSchedule>(null)

  // Use shared unsaved changes hook
  const {
    showCancelDialog,
    handleCancelClick,
    handleCancelConfirm,
    handleCancelDismiss,
  } = useUnsavedChangesWarning(isDirty)

  const handleCancel = () => {
    if (componentRef.current) {
      ;(componentRef.current as any).cancel()
    }
  }

  return (
    <>
      <InjectedEditSchedule
        routeParams={params}
        ref={componentRef}
        showCancelDialog={showCancelDialog}
        handleCancelClick={handleCancelClick}
        handleCancelConfirm={(onNavigate) => {
          handleCancelConfirm(onNavigate)
        }}
        handleCancelDismiss={handleCancelDismiss}
        setIsDirty={setIsDirty}
      />
      <UnsavedChangesDialog
        open={showCancelDialog}
        onConfirm={() => handleCancelConfirm(handleCancel)}
        onDismiss={handleCancelDismiss}
      />
    </>
  )
}

export default EditScheduleWithRouter
